import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import api from '../../services/api';

// Assets
import bedroom from '../../assets/images/bedroom.png';
//import decor from '../../assets/images/decor.png';
import heroChair from '../../assets/images/hero_luxury_chair.png';
//import lighting from '../../assets/images/lighting.png';
import living from '../../assets/images/living_room.png';

const initialSlides = [
  {
    image: heroChair,
    tag: "Nouvelle Collection ",
    title: "Esprit Moderne",
    description: "Découvrez le mélange parfait d'artisanat, de confort et de design contemporain. Notre collection soigneusement sélectionnée apporte une élégance intemporelle à chaque coin de votre maison.",
  },
  {
    image: living,
    tag: "Intérieurs Sur Mesure",
    title: "Conçu pour le Confort",
    description: "Transformez votre salon en un sanctuaire de style. Découvrez des pièces qui parlent à votre âme et subliment vos expériences quotidiennes.",
  },
  {
    image: bedroom,
    tag: "Repos Élégant",
    title: "Sanctuaire Intemporel",
    description: "Créez une chambre qui inspire la paix et la tranquillité. Les matériaux de luxe rencontrent le design minimaliste pour une relaxation ultime.",
  }
];

const Carousel = ({ autoPlay = true, interval = 6000 }) => {
  const [index, setIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/products/');
        setProducts(response.data.slice(0, 3));
      } catch (err) {
        console.error('Error fetching carousel products:', err);
      }
    };
    fetchProducts();
  }, []);

  const handleNext = React.useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIndex((i) => (i + 1) % initialSlides.length);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating]);

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      handleNext();
    }, interval);
    return () => clearInterval(id);
  }, [autoPlay, interval, index, handleNext]);

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIndex((i) => (i - 1 + initialSlides.length) % initialSlides.length);
    setTimeout(() => setIsAnimating(false), 800);
  };

  return (
    <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden bg-slate-900">
      {/* Slides container */}
      <div className="absolute inset-0 w-full h-full">
        {initialSlides.map((slide, i) => {
          const product = products[i];
          return (
            <div
              key={i}
              className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out transform ${i === index ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-105 invisible'
                }`}
            >
              {/* Image with Dark Overlay */}

              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />

              {/* Text Content Overlay */}
              <div className="absolute inset-0 z-20 flex items-center pt-24 sm:pt-32">
                <div className="container mx-auto px-2">
                  <div className="max-w-3xl transform transition-all duration-700 delay-300">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 mb-6 transition-all duration-700 ${i === index ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                      <span className="w-2 h-2 rounded-full bg-brand animate-pulse"></span>
                      <span className="text-xs font-bold tracking-widest uppercase text-brand-dark">{slide.tag}</span>
                    </div>

                    <h1 className={`text-4xl sm:text-6xl md:text-6xl font-extrabold text-brand-dark leading-[1.1] mb-6 transition-all duration-700 delay-100 ${i === index ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                      {slide.title}
                    </h1>

                    <div className={`p-6 rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 mb-10 max-w-xl transition-all duration-700 delay-200 ${i === index ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                      <p className="text-lg sm:text-xl text-white leading-relaxed">
                        {slide.description}
                      </p>
                    </div>

                    <div className={`flex flex-col sm:flex-row items-center gap-4 transition-all duration-700 delay-300 ${i === index ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                      <Button variant="primary" className="h-14 px-8 text-lg w-full sm:w-auto">
                        Explorez la Collection
                        <ArrowRight className="w-5 h-5 ml-1" />
                      </Button>

                      <button className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 transition-all group w-full sm:w-auto justify-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand text-white shadow-lg shadow-brand/20 group-hover:scale-110 transition-transform">
                          <Play className="w-4 h-4 fill-current ml-0.5" />
                        </div>
                        <span className="text-sm font-semibold text-white">Voir le Film</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Product Tag (Optional/Contextual) */}
              {product && (
                <Link
                  to={`/product/${product.id}`}
                  className={`hidden lg:block absolute bottom-12 right-12 z-30 bg-white/10 backdrop-blur-xl p-6 rounded-3xl border border-white/20 transition-all duration-1000 delay-500 hover:bg-white/20 group/tag ${i === index ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'}`}
                >
                  <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mb-1">Article en Vedette</p>
                  <p className="text-lg font-bold text-white leading-none mb-4 group-hover/tag:text-brand transition-colors">{product.name}</p>
                  <div className="flex items-center justify-between gap-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                      <span className="text-xl font-bold text-white">{Math.floor(product.price).toLocaleString('fr-FR')} FCFA</span>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center bg-white text-brand rounded-full group-hover/tag:bg-brand group-hover/tag:text-white transition-all transform group-hover/tag:rotate-12">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <div className="absolute inset-y-0 left-0 right-0 z-40 flex items-center justify-between px-4 pointer-events-none">
        <button
          onClick={handlePrev}
          className="p-3 rounded-full bg-white/5 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white transition-all pointer-events-auto transform hover:scale-110 active:scale-95"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={handleNext}
          className="p-3 rounded-full bg-white/5 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white transition-all pointer-events-auto transform hover:scale-110 active:scale-95"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Dots navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex gap-3">
        {initialSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => !isAnimating && setIndex(i)}
            className={`h-1.5 transition-all duration-500 rounded-full ${i === index ? 'w-8 bg-brand' : 'w-2 bg-white/30 hover:bg-white/50'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Bottom Gradient for smoother transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-green-800/70 via-green-700/30 to-transparent z-30 pointer-events-none"></div>
    </section>
  );
};

export default Carousel;
